/**
 * Offline-first sync engine for AMAL VBC.
 * - Caches every GET into IndexedDB.
 * - Queues every write while offline; replays them in order when back online.
 * - Detects conflicts (HTTP 409) and stores them for user resolution.
 */
import {
  enqueueWrite,
  listQueue,
  removeFromQueue,
  updateQueueItem,
  queueCount,
  cacheRead,
  getCachedRead,
  clearReadCacheForPath,
  addConflict,
} from "@/lib/idb";

// Endpoints that should never be cached or queued (auth, file uploads, PDF downloads).
const SKIP_PATHS = [
  "/auth/login",
  "/auth/logout",
  "/documents/generate",
  "/finance/annual-report",
  "/system/backup",
  "/system/manual",
  "/storage/upload",
];

export function shouldSkip(url) {
  return SKIP_PATHS.some((p) => url.includes(p));
}

function cacheKey(method, url) {
  return `${method.toUpperCase()} ${url}`;
}

// --------------- Listeners + state ---------------
const listeners = new Set();
let isSyncing = false;

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function emit() {
  const count = await queueCount();
  const online = navigator.onLine;
  listeners.forEach((fn) => fn({ online, pending: count, syncing: isSyncing }));
}

export async function getStatus() {
  return { online: navigator.onLine, pending: await queueCount(), syncing: isSyncing };
}

// --------------- Public helpers used by axios interceptors ---------------
export async function recordReadCache(method, url, data) {
  try {
    await cacheRead(cacheKey(method, url), data);
  } catch (e) {
    // Quota exceeded — silently ignore
  }
}

export async function serveFromCache(method, url) {
  return await getCachedRead(cacheKey(method, url));
}

export async function invalidatePath(pathRoot) {
  // strip query, keep base path
  const base = pathRoot.split("?")[0];
  await clearReadCacheForPath(base);
}

export async function enqueueOperation({ method, url, data, headers }) {
  const id = await enqueueWrite({ method, url, data, headers });
  await emit();
  return id;
}

// --------------- Sync engine ---------------
let _apiRef = null;
export function bindApi(api) {
  _apiRef = api;
}

export async function syncQueue() {
  if (isSyncing || !navigator.onLine || !_apiRef) return;
  isSyncing = true;
  await emit();

  const items = await listQueue();
  let success = 0;
  let conflicts = 0;
  let failed = 0;

  for (const item of items) {
    try {
      const cfg = {
        method: item.method,
        url: item.url,
        data: item.data,
        headers: { ...(item.headers || {}), "X-Offline-Replay": "1" },
      };
      await _apiRef.request(cfg);
      await removeFromQueue(item.id);
      // Invalidate cached lists touched by this write
      await invalidatePath(item.url.split("/").slice(0, 3).join("/"));
      success += 1;
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Conflict — record + drop from queue
        await addConflict({
          opId: item.id,
          method: item.method,
          url: item.url,
          local: item.data,
          server: err.response?.data,
        });
        await removeFromQueue(item.id);
        conflicts += 1;
      } else if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        // Client error (validation, not found) — drop, don't retry forever
        await removeFromQueue(item.id);
        failed += 1;
      } else {
        // Network or 5xx — keep, increment attempts
        await updateQueueItem(item.id, { attempts: (item.attempts || 0) + 1, lastError: err.message });
        failed += 1;
        // Stop processing further to avoid hammering
        break;
      }
    }
  }

  isSyncing = false;
  await emit();
  return { success, conflicts, failed };
}

// --------------- Boot listeners ---------------
export function initOfflineSync(api) {
  bindApi(api);
  window.addEventListener("online", () => syncQueue());
  window.addEventListener("offline", () => emit());
  // initial emit
  setTimeout(emit, 100);
  // Periodic background retry every 30s while online and queue not empty
  setInterval(async () => {
    if (navigator.onLine && (await queueCount()) > 0) syncQueue();
  }, 30000);
}
